import { useState } from 'react';

import { api } from './api';
import { signOut, type Session } from './auth';
import type { PortalConfig } from './config';
import { AsyncBoundary, BrandMark, useAsync } from './ui';
import { AppsView } from './views/AppsView';
import { CodeFindingsView } from './views/CodeFindingsView';
import { OverviewView } from './views/OverviewView';
import { PentestsView } from './views/PentestsView';
import { RepositoriesView } from './views/RepositoriesView';

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'apps', label: 'Apps' },
  { id: 'repositories', label: 'Repositories' },
  { id: 'code', label: 'Code findings' },
  { id: 'pentests', label: 'Pen tests' },
] as const;

type TabId = (typeof TABS)[number]['id'];

export function App({ config, session }: { config: PortalConfig; session: Session }) {
  const [tab, setTab] = useState<TabId>('overview');

  // Loaded once: which permissions the signed-in user has, and the domain apps must
  // sit under. Every write is re-checked server side, so this only decides what the
  // UI offers.
  const viewer = useAsync(() => api.me(config), [config]);

  return (
    <div className="app">
      <header className="topbar">
        <div className="brand">
          <BrandMark />
          Lighthouse <span>{config.environment}</span>
        </div>
        <div className="topbar-spacer" />
        <div className="user">{session.email}</div>
        <button className="action secondary" onClick={() => signOut(config)}>
          Sign out
        </button>
      </header>

      <nav className="tabs" role="tablist">
        {TABS.map((candidate) => (
          <button
            key={candidate.id}
            role="tab"
            className="tab"
            aria-selected={tab === candidate.id}
            onClick={() => setTab(candidate.id)}
          >
            {candidate.label}
          </button>
        ))}
      </nav>

      <main>
        {tab === 'overview' ? <OverviewView config={config} /> : null}
        {tab === 'repositories' ? <RepositoriesView config={config} /> : null}
        {/* Findings are readable by anyone, so this does not wait on the viewer the
            way the write-heavy tabs do; dismissing is hidden until it loads. */}
        {tab === 'code' ? <CodeFindingsView config={config} isAdmin={viewer.data?.isAdmin ?? false} /> : null}

        {tab === 'apps' ? (
          <AsyncBoundary state={viewer}>{(data) => <AppsView config={config} viewer={data} />}</AsyncBoundary>
        ) : null}

        {tab === 'pentests' ? (
          <AsyncBoundary state={viewer}>{(data) => <PentestsView config={config} viewer={data} />}</AsyncBoundary>
        ) : null}
      </main>
    </div>
  );
}
